import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SearchAccountsRequest } from '../models/search-accounts-request.model';
import { AppConfigService } from 'src/app/core/app-config/app-config.service';
import { CustomerAccountsRequest } from '../models/customer-accounts-request.model';
import { Operation } from '../models/operation.model';
import { AccountContact } from '../models/account-contact.model';

@Injectable({
    providedIn: 'root'
})
export class UserManagementDataService {

    constructor(
        private httpClient: HttpClient,
        private appConfigService: AppConfigService) {
    }

    getUserData() {
        const apiUrl = 'users/details';
        return this.getData<any>(apiUrl);
    }

    getUsersData(userId: string) {
        const apiUrl = 'users?userIds=' + userId;
        return this.getData<any>(apiUrl);
    }

    getUserPermission(isInternalUser: boolean) {
        const apiUrl = isInternalUser ? 'users/me/eurofinsLegalEntities' : 'users/me/accounts/permissions';
        return this.getData<any>(apiUrl);
    }

    getUserApplicationPermissions(applicationCode: string) {
        const apiUrl = 'applications/' + applicationCode + '/users/me/operations';
        return this.getData<any>(apiUrl);
    }

    getInternalUserAccounts(searchReq: SearchAccountsRequest) {
        const apiUrl = 'accounts/search';
        return this.getDataByFilter<any>(apiUrl, searchReq);
    }

    getCustomerUserAccounts(customerAccountsRequest: CustomerAccountsRequest, cache = false, loading = true): Observable<any> {
        const apiUrl = 'accounts';
        return this.getDataByAccountCodes<any>(apiUrl, customerAccountsRequest, cache, loading);
    }

    getCanViewPricePermission(isInternalUser: boolean, accountCode: string): Observable<Operation> {
        const applicationCode = 'EOL';
        const operationPermissionScope = isInternalUser ? 'Application' : 'Account';
        const operationName = 'CanViewPrices';
        const apiUrl = `applications/${applicationCode}/users/me/operations?operationPermissionScope=
        ${operationPermissionScope}&operationNames=${operationName}&accountCodes=${accountCode}`;
        return this.getData<Operation>(apiUrl);
    }

    getAccountContacts(accountCode: string): Observable<AccountContact[]> {
        const apiUrl = 'users/me/accounts/' + accountCode + '/contacts';
        return this.getData<AccountContact[]>(apiUrl);
    }

    getAccountByAccountCode(accountCode: string): Observable<any> {
        const apiUrl = 'accounts?accountCodes=' + accountCode;
        return this.getData<any[]>(apiUrl);
    }

    getAccountsByAccountCodes(accountCodes: string[]): Observable<any[]> {
        const apiUrl = 'accounts';
        const params = accountCodes.map(code => `accountCodes=${code}`).join('&');
        const url = this.appConfigService.configuration.apiConfig.userManagementQueryBaseUrl + apiUrl + '?' + params;
        return this.httpClient.get<any[]>(url);
    }

    private getData<T>(apiUrl: string): Observable<T> {
        const url = this.appConfigService.configuration.apiConfig.userManagementQueryBaseUrl + apiUrl;
        return this.httpClient.get<T>(url);
    }

    private getDataByAccountCodes<T>(
        apiUrl: string, customerAccountsRequest: CustomerAccountsRequest,
        cache = false, loading = true): Observable<T> {
        const url = this.appConfigService.configuration.apiConfig.userManagementQueryBaseUrl + apiUrl;
        let headers: HttpHeaders;
        if (cache) {
            headers = new HttpHeaders({ 'cache-response': 'true' });
        }
        if (!loading) {
            headers = headers.append('no-loading', 'true');
        }
        return this.httpClient.get<T>(url, {
            params: {
                accountCodes: customerAccountsRequest.accountCodes
            }
        });
    }

    private getDataByFilter<T>(apiUrl: string, searchRequest: SearchAccountsRequest): Observable<T> {
        const url = this.appConfigService.configuration.apiConfig.userManagementQueryBaseUrl + apiUrl;
        return this.httpClient.get<T>(url, {
            params: {
                sortColumn: searchRequest.sortColumn,
                sortDirection: searchRequest.sortDirection.toString(),
                pageNumber: searchRequest.pageNumber.toString(),
                recordsCount: searchRequest.recordsCount.toString(),
                SearchText: searchRequest.searchText,
                pageSize: searchRequest.pageSize.toString(),
                eleCodes: searchRequest.eleCodes,
            }
        });
    }
}
